<template>
  <div ref="bar" :class="'j-tabs-bar column-tabs' + (overflowing ? ' column-tabs-overflowing' : '') + (gridOn ? ' column-tabs-pills' : '')" :data-grid="gridOn ? grid : undefined">
    <div
      ref="list"
      class="j-tabs"
      role="tablist"
      :style="gridStyle"
      :aria-label="label || _('Tabs')"
      v-on:keydown="onKeydown"
      v-on:focusin="onFocusin"
      v-on:focusout="onFocusout"
    >
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="'column-tab component' + (tab.id === selectedId ? ' active' : '')"
        :data-tab-id="tab.id"
        :hidden="!visibleSet.has(tab.id)"
      >
        <button
          :id="tabElementId(tab.id)"
          class="j-tab"
          type="button"
          role="tab"
          :aria-selected="tab.id === selectedId ? 'true' : 'false'"
          :aria-controls="panelId || undefined"
          :tabindex="tabindexFor(tab.id)"
          :aria-describedby="tip && tip.id === tab.id ? tipId : undefined"
          v-on:click.stop="onTabClick(tab.id, $event)"
          v-on:pointerdown="onTabPointerdown(tab.id, $event)"
          v-on:pointermove="onTabPointermove(tab.id, $event)"
          v-on:pointerup="endPress"
          v-on:pointercancel="endPress"
          v-on:pointerenter="onTabPointerenter(tab.id, $event)"
          v-on:pointerleave="onTabPointerleave(tab.id, $event)"
          v-on:contextmenu="onTabContextmenu"
          v-on:focus="onTabFocus(tab.id, $event)"
          v-on:blur="hideTip(tab.id)"
        >
          <span class="j-tab-label component-tab-label">{{ tab.label }}</span>
          <slot name="tab-extra" :tab="tab" :selected="tab.id === selectedId" :in-menu="false"></slot>
        </button>
        <slot name="tab-actions" :tab="tab" :selected="tab.id === selectedId" :in-menu="false" :tabindex="tab.id === selectedId ? 0 : -1"></slot>
      </div>
      <!-- Button grid (JDEV-3370): the add (+) button is the grid's last cell. -->
      <button v-if="showAdd && gridOn" class="column-tabs-add" type="button" :title="addLabel" :aria-label="addLabel" v-on:click.stop="emit('add')">
        <i class="fa fa-fw fa-plus" aria-hidden="true"></i>
      </button>
    </div>
    <button v-if="showAdd && !gridOn" ref="addEl" class="column-tabs-add" type="button" :title="addLabel" :aria-label="addLabel" v-on:click.stop="emit('add')">
      <i class="fa fa-fw fa-plus" aria-hidden="true"></i>
    </button>
    <TabOverflowMenu
      ref="menu"
      :items="menuItems"
      :checked-id="selectedId"
      :label="moreLabel"
      :aria-label="moreAriaLabel"
      :menu-id="menuId"
      v-on:select="onMenuSelect"
    >
      <template #item="{ item, checked }">
        <span class="column-tabs-menu-label">{{ item.label }}</span>
        <slot name="tab-extra" :tab="tabById(item.id)" :selected="checked" :in-menu="true"></slot>
      </template>
      <template #item-after="{ item, checked }">
        <slot name="tab-actions" :tab="tabById(item.id)" :selected="checked" :in-menu="true" :tabindex="-1"></slot>
      </template>
    </TabOverflowMenu>
    <!-- The full label of a truncated tab: while it is long-pressed (touch), hovered (mouse) or keyboard-focused.
         Beside the tab (never above or below it, where the finger is), on the side with more room, with an arrow
         whose tip overlaps the tab. In <body>, because .pos-column clips its overflow. -->
    <Teleport to="body">
      <div v-if="tip" :id="tipId" ref="tipEl" class="column-tab-tip" role="tooltip" :lang="tipLang" :data-side="tip.side" :style="tipStyle">{{ tip.label }}</div>
    </Teleport>
  </div>
</template>

<script lang="ts">
// Each column component renders its own ColumnHeader (lib/components.js), so selecting a tab mounts a new
// header and the focused tab button is gone. The tab to focus is handed to the next ColumnTabs of that panel.
let pendingFocus: { panelId: string, id: string, at: number } | null = null
</script>

<script setup lang="ts">
// The column header's tab strip in normal mode (JDEV-3207), rendered by ColumnHeader.vue (layout edit mode
// keeps ColumnHeader's draggable list). One row, never wraps: priority+ with a "Lisää (n)" menu pinned to the
// bar's inline end (useTabOverflow), WAI-ARIA tabs with roving tabindex and manual activation (useRovingTabs).
// Markup: shared/styles tabs.css "More" bar; App-3 skin in johkupos-main-app.scss (.header-navbar .column-tabs).
//
// Props:
//   tabs        [{ id: string, label: string, ... }] in order (extra fields are passed back in the slots)
//   selectedId  the selected tab (never hidden; it truncates if it alone does not fit)
//   panelId     id of the tab panel (the column root, role=tabpanel from lib/components.js): aria-controls,
//               and the tab ids are `${panelId}-tab-${id}` (the panel's aria-labelledby uses the same rule)
//   label       the tablist's aria-label (default: "Tabs")
//   showAdd     render the add (+) button right after the visible tabs; addLabel is its accessible name
//   layoutKey   anything that changes a tab's width without changing its label (counters, icons): re-fits
//   grid        null (default): the one-row strip above. "grid" / "gridfixed" (JDEV-3370, the user interface
//               setting "Column tabs"; ColumnHeader passes it only from 768px up): every tab is a pill in a grid
//               on as many rows as needed, with no "Lisää" menu; the add (+) button is the last cell. The column
//               count comes from useTabGrid: one content-sized row while every label fits untruncated, else
//               cells of at least 150px or a fixed count by width, never more columns than cells and never a
//               single cell alone on the last row when the rows above can take it. Up/Down move between the rows.
// Events:   select(id) on click / Enter / Space or a menu row (not for the selected tab), add
// Slots:    tab-extra { tab, selected, inMenu }  non-interactive content after the label, inside the tab and
//                                                inside the menu row (e.g. the Receipt counter)
// Truncation: below 1024px (TRUNCATE_QUERY, phones and tablets) tabs truncate down to a minimum width before they go to the menu
//           (useTabOverflow `truncate`). A truncated tab shows its full label in a tooltip while it is long-pressed
//           (touch: no selection, context menu or text selection), hovered or keyboard-focused. Its
//           accessible name is always the full label (the label text is in the button).
//           tab-actions { tab, selected, inMenu, tabindex }  buttons next to the tab / menu row, never inside
//                                                them (edit pencil, close, delivery toggle). Use @click.stop.
// Exposes:  refit()

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import TabOverflowMenu from '~/components/ui/TabOverflowMenu.vue'
import { useRovingTabs } from '~/composables/useRovingTabs'
import { useTabOverflow } from '~/composables/useTabOverflow'
import { useTabGrid, type ColumnTabsMode } from '~/composables/useTabGrid'

export interface ColumnTab {
  id: string
  label: string
  [key: string]: unknown
}

const props = withDefaults(defineProps<{
  tabs: ColumnTab[]
  selectedId?: string | null
  panelId?: string
  label?: string
  showAdd?: boolean
  addLabel?: string
  layoutKey?: string | number
  grid?: ColumnTabsMode | null
}>(), { selectedId: null, panelId: '', label: '', showAdd: false, addLabel: '', layoutKey: '', grid: null })

const emit = defineEmits<{
  (e: 'select', id: string): void
  (e: 'add'): void
}>()

const { $translate, $store } = useNuxtApp() as unknown as { $translate: (key: string) => string, $store: { state: { locale?: string } } }
const _ = (key: string) => $translate(key)
// A "context|Text" key (pgettext in the backend's pos-strings.c) shows its text part until it is translated:
// an untranslated locale gets the key back, or "context\u0004Text" from gettext.
const ctx = (key: string) => {
  const t = _(key)
  return t === key || t.includes('\u0004') ? key.slice(key.indexOf('|') + 1) : t
}

const bar = ref<HTMLElement | null>(null)
const list = ref<HTMLElement | null>(null)
const addEl = ref<HTMLElement | null>(null)
const menu = ref<InstanceType<typeof TabOverflowMenu> | null>(null)
const toggle = computed(() => (menu.value?.toggleEl as HTMLElement | undefined) ?? null)

const ids = computed(() => props.tabs.map(t => t.id))
const selected = computed(() => props.selectedId)

const TRUNCATE_QUERY = '(max-width: 1023px)'
const truncate = ref(false)
let mql: MediaQueryList | null = null
const onMedia = () => { truncate.value = !!mql?.matches }
onMounted(() => {
  mql = window.matchMedia(TRUNCATE_QUERY)
  onMedia()
  mql.addEventListener('change', onMedia)
})

// Button grid (JDEV-3370): every tab is shown, so the priority+ fit is off.
const gridOn = computed(() => props.grid === 'grid' || props.grid === 'gridfixed')
const gridMode = computed(() => (gridOn.value ? props.grid : null))
const { columns: gridColumnCount, oneRow: gridOneRow } = useTabGrid({
  bar,
  list,
  mode: gridMode,
  count: computed(() => props.tabs.length + (props.showAdd ? 1 : 0)),
  watch: [() => props.tabs.map(t => t.label).join('\u0000'), () => props.layoutKey]
})
// One row (JDEV-3379): the columns are sized by their content, so every label fits while there is room and the
// spare width is shared out; the add (+) button keeps its square. More rows: equal columns (the CSS default).
const gridStyle = computed(() => {
  if (!gridOn.value || !gridColumnCount.value) return undefined
  if (!gridOneRow.value) return { '--column-tabs-cols': gridColumnCount.value }
  return { gridTemplateColumns: `repeat(${props.tabs.length}, minmax(0, auto))` + (props.showAdd ? ' 32px' : '') }
})

const { visibleIds, hiddenIds, overflowing, refit } = useTabOverflow({
  enabled: computed(() => !gridOn.value),
  truncate,
  bar,
  list,
  toggle,
  ids,
  selectedId: selected,
  reserve: [addEl],
  watch: [() => props.tabs.map(t => t.label).join('\u0000'), () => props.layoutKey, () => props.showAdd]
})
const visibleSet = computed(() => new Set(visibleIds.value))

const { tabindexFor, onKeydown, onFocusin, onFocusout, focusTab } = useRovingTabs({
  list,
  ids: visibleIds,
  selectedId: selected,
  onActivate: id => activate(id),
  columns: gridColumnCount
})

const tabById = (id: string) => props.tabs.find(t => t.id === id) as ColumnTab
const tabElementId = (id: string) => (props.panelId ? `${props.panelId}-tab-${id}` : undefined)
const menuId = computed(() => `${props.panelId || 'column'}-tabs-more`)
const menuItems = computed(() => hiddenIds.value.map(id => ({ id, label: tabById(id)?.label ?? id })))
const moreLabel = computed(() => ctx('tabs|More'))
const moreAriaLabel = computed(() => _('More, %d tabs hidden').replace('%d', String(hiddenIds.value.length)))

// --- Full label of a truncated tab -------------------------------------------------------------------------
const LONG_PRESS_MS = 450
const HOVER_MS = 400
const MOVE_CANCEL_PX = 10
const tipEl = ref<HTMLElement | null>(null)
// The UI locale as a BCP 47 tag (fi_FI -> fi-FI) on the box, so hyphens: auto uses that language's rules.
const tipLang = computed(() => ($store?.state?.locale || 'fi_FI').replace('_', '-'))
interface TabTip { id: string, label: string, side: 'left' | 'right', x: number, top: number, maxWidth: number, arrowY: number, centerY: number }
const tip = ref<TabTip | null>(null)
const tipId = computed(() => `${props.panelId || 'column'}-tabs-tip`)
// x is the box's edge next to the tab: its left edge (side "right") or its right edge (side "left", the CSS moves
// the box left by its own width). Not `right:`, which would count from beyond a page scrollbar.
const tipStyle = computed(() => {
  const t = tip.value
  if (!t) return {}
  return { left: t.x + 'px', top: t.top + 'px', maxWidth: t.maxWidth + 'px', '--tip-arrow-y': t.arrowY + 'px' }
})
let pressTimer: ReturnType<typeof setTimeout> | null = null
let hoverTimer: ReturnType<typeof setTimeout> | null = null
let press: { id: string, x: number, y: number } | null = null
let longPressed: string | null = null
let lastPointerType = 'mouse'
let hoverDone: string | null = null // clicked while hovered: no box again until the mouse leaves the tab

function tabButton (id: string) {
  return list.value?.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(id)}"] [role="tab"]`) ?? null
}
function isTruncated (id: string) {
  const label = tabButton(id)?.querySelector<HTMLElement>('.j-tab-label')
  return !!label && label.scrollWidth > label.clientWidth + 1
}
const TIP_MARGIN = 8 // px from the viewport edges
const TIP_ARROW = 8 // px, the arrow's length (its base is 2 * TIP_ARROW)
const TIP_OVERLAP = 12 // px, how far the arrow's tip reaches into the tab
const TIP_MIN_WIDTH = 120 // px; with less room beside the tab the box moves over the tab (the arrow still points at it)

function showTip (id: string) {
  const btn = tabButton(id)
  if (!btn || !isTruncated(id)) return
  const r = (btn.closest('.column-tab') || btn).getBoundingClientRect()
  const vw = document.documentElement.clientWidth || window.innerWidth
  const tipRight = r.right - TIP_OVERLAP // arrow tip when the box opens to the right
  const tipLeft = r.left + TIP_OVERLAP //   … and to the left
  const roomRight = vw - TIP_MARGIN - (tipRight + TIP_ARROW)
  const roomLeft = tipLeft - TIP_ARROW - TIP_MARGIN
  const side = roomRight >= roomLeft ? 'right' : 'left'
  let x: number
  let maxWidth: number
  if (side === 'right') {
    const left = Math.min(tipRight + TIP_ARROW, vw - TIP_MARGIN - TIP_MIN_WIDTH)
    x = left
    maxWidth = vw - TIP_MARGIN - left
  } else {
    const right = Math.max(tipLeft - TIP_ARROW, TIP_MARGIN + TIP_MIN_WIDTH)
    x = right
    maxWidth = right - TIP_MARGIN
  }
  const centerY = r.top + r.height / 2
  tip.value = { id, label: tabById(id)?.label ?? '', side, x, top: centerY, maxWidth, arrowY: 0, centerY }
  // Once its height is known (the label may wrap): centre it on the tab, inside the viewport; the arrow follows the tab.
  nextTick(() => {
    const el = tipEl.value
    const t = tip.value
    if (!el || !t || t.id !== id) return
    const h = el.getBoundingClientRect().height
    const vh = document.documentElement.clientHeight || window.innerHeight
    const top = Math.max(TIP_MARGIN, Math.min(t.centerY - h / 2, vh - TIP_MARGIN - h))
    const arrowY = Math.max(TIP_ARROW + 2, Math.min(t.centerY - top, h - TIP_ARROW - 2))
    tip.value = { ...t, top, arrowY }
  })
}
function hideTip (id?: string) {
  if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null }
  if (!id || tip.value?.id === id) tip.value = null
}
function onTabPointerdown (id: string, e: PointerEvent) {
  lastPointerType = e.pointerType
  longPressed = null
  hideTip()
  if (e.pointerType === 'mouse') return
  press = { id, x: e.clientX, y: e.clientY }
  if (pressTimer) clearTimeout(pressTimer)
  pressTimer = setTimeout(() => {
    pressTimer = null
    if (press?.id === id && isTruncated(id)) {
      longPressed = id
      showTip(id)
    }
  }, LONG_PRESS_MS)
}
// A finger that moves (a scroll or swipe that starts on a tab) cancels the long press. A mouse that moves over a tab
// without having entered it (the strip re-fitted under the pointer, so the tab only now truncates) starts the hover.
function onTabPointermove (id: string, e: PointerEvent) {
  if (e.pointerType === 'mouse') {
    if (!e.buttons && !hoverTimer && hoverDone !== id && tip.value?.id !== id) onTabPointerenter(id, e)
    return
  }
  if (press && !longPressed && Math.hypot(e.clientX - press.x, e.clientY - press.y) > MOVE_CANCEL_PX) endPress()
}
// Release (or cancel): the tooltip goes; the click that may follow a long press is swallowed in onTabClick.
function endPress () {
  if (pressTimer) { clearTimeout(pressTimer); pressTimer = null }
  press = null
  if (longPressed) hideTip(longPressed)
}
function onTabContextmenu (e: Event) {
  // A touch long press opens the browser's context menu (or a link preview) otherwise.
  if (lastPointerType !== 'mouse' || longPressed) e.preventDefault()
}
function onTabPointerenter (id: string, e: PointerEvent) {
  if (e.pointerType !== 'mouse' || tip.value?.id === id) return
  if (hoverTimer) clearTimeout(hoverTimer)
  hoverTimer = setTimeout(() => { hoverTimer = null; showTip(id) }, HOVER_MS)
}
function onTabPointerleave (id: string, e: PointerEvent) {
  if (hoverDone === id) hoverDone = null
  if (e.pointerType === 'mouse') hideTip(id)
}
function onTabFocus (id: string, e: FocusEvent) {
  const el = e.target as HTMLElement
  if (el.matches(':focus-visible')) showTip(id)
}
function onTabClick (id: string, e: MouseEvent) {
  if (longPressed === id) {
    e.preventDefault()
    longPressed = null
    return
  }
  if (lastPointerType === 'mouse') hoverDone = id
  hideTip()
  activate(id)
}
function onTipKeydown (e: KeyboardEvent) {
  if (e.key === 'Escape' && tip.value) hideTip()
}
onMounted(() => document.addEventListener('keydown', onTipKeydown, true))
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onTipKeydown, true)
  mql?.removeEventListener('change', onMedia)
  if (pressTimer) clearTimeout(pressTimer)
  if (hoverTimer) clearTimeout(hoverTimer)
})

function activate (id: string) {
  if (id === props.selectedId) return
  const hadFocus = !!list.value && list.value.contains(document.activeElement)
  if (hadFocus) pendingFocus = { panelId: props.panelId, id, at: Date.now() }
  emit('select', id)
  // The column swaps its component, which can drop the focus to <body>; keep it on the activated tab.
  if (hadFocus) {
    nextTick(() => requestAnimationFrame(() => {
      const active = document.activeElement
      if (!active || active === document.body) focusTab(id)
    }))
  }
}

async function onMenuSelect (id: string) {
  if (id !== props.selectedId) pendingFocus = { panelId: props.panelId, id, at: Date.now() }
  activate(id)
  // The selected tab is swapped into the row; give it focus once it is shown.
  await nextTick()
  requestAnimationFrame(() => focusTab(id))
}

// The panel id is set by ColumnHeader's mounted hook, after this component has mounted.
watch(() => props.panelId, (panelId) => {
  const p = pendingFocus
  if (!p || !panelId || p.panelId !== panelId) return
  pendingFocus = null
  if (Date.now() - p.at > 3000 || p.id !== props.selectedId) return
  nextTick(() => requestAnimationFrame(() => {
    const active = document.activeElement
    if (!active || active === document.body) focusTab(p.id)
  }))
}, { immediate: true })

defineExpose({ refit })
</script>
